import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Handshake, CheckCircle2, Lock, Crown, X, AlertTriangle, Infinity as InfinityIcon } from "lucide-react";
import { PUBLIC_SITE_URL } from "@/lib/publicUrl";

export default function PartnershipCard() {
    const [status, setStatus] = useState(null);
    const [slots, setSlots] = useState(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [slotsBusy, setSlotsBusy] = useState(false);
    const [feedback, setFeedback] = useState(null); // {type: success|error, msg}

    const load = async () => {
        try {
            const { data } = await api.get("/agency/partnership/status");
            setStatus(data);
            if (data.premium_active && !data.is_partner) {
                try {
                    const { data: slotsData } = await api.get("/agency/listing-slots/status");
                    setSlots(slotsData);
                } catch (e) { setSlots(null); }
            } else {
                setSlots(null);
            }
        } catch (e) {
            // 403 se non è agenzia — nasconde la card
            setStatus({ hidden: true });
        }
    };

    useEffect(() => { load(); }, []);

    const redeem = async (e) => {
        e?.preventDefault?.();
        if (!code.trim()) return;
        setBusy(true); setFeedback(null);
        try {
            const { data } = await api.post("/agency/partnership/redeem", { code: code.trim() });
            setFeedback({ type: "success", msg: data.message || "Codice attivato" });
            setCode("");
            await load();
        } catch (err) {
            setFeedback({ type: "error", msg: err?.response?.data?.detail || "Impossibile attivare il codice" });
        } finally { setBusy(false); }
    };

    const revoke = async () => {
        if (!window.confirm(`Vuoi disattivare la Partnership? Tornerai a Standard (${status.included_slots} slot inclusi). Puoi riattivarla in seguito con lo stesso codice.`)) return;
        setBusy(true); setFeedback(null);
        try {
            await api.post("/agency/partnership/revoke");
            setFeedback({ type: "success", msg: "Partnership disattivata. Sei tornato a Standard." });
            await load();
        } catch (err) {
            setFeedback({ type: "error", msg: err?.response?.data?.detail || "Errore" });
        } finally { setBusy(false); }
    };

    const buySlots = async (tier) => {
        setSlotsBusy(true); setFeedback(null);
        try {
            const { data } = await api.post("/agency/listing-slots/checkout", { origin_url: PUBLIC_SITE_URL, tier });
            window.location.href = data.url;
        } catch (err) {
            setFeedback({ type: "error", msg: err?.response?.data?.detail || "Impossibile avviare il pagamento" });
            setSlotsBusy(false);
        }
    };

    const cancelSlots = async () => {
        if (!window.confirm(`Disdire il pacchetto Slot Extra? Resterà attivo fino a fine periodo, poi tornerai a ${status.included_slots} slot.`)) return;
        setSlotsBusy(true); setFeedback(null);
        try {
            const { data } = await api.post("/agency/listing-slots/cancel");
            setFeedback({ type: "success", msg: data.message || "Disdetta registrata" });
            await load();
        } catch (err) {
            setFeedback({ type: "error", msg: err?.response?.data?.detail || "Errore" });
        } finally { setSlotsBusy(false); }
    };

    if (!status || status.hidden || status.role !== "agenzia" || status.plan_id === "agenzia") return null;

    const isPartner = !!status.is_partner;
    const premiumActive = !!status.premium_active;
    const decay = status.decay_reason;

    return (
        <div data-testid="partnership-card" className="mb-16 border border-white/10 bg-ink-900/30 p-6 lg:p-8">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
                <div className="flex items-start gap-3">
                    <div className={`w-11 h-11 flex items-center justify-center border ${isPartner ? "border-gold/60 bg-gold/10 text-gold" : "border-white/20 text-ink-100"}`}>
                        <Handshake size={20} />
                    </div>
                    <div>
                        <span className="label-eyebrow">Programma Partnership</span>
                        <h2 className="font-serif text-2xl sm:text-3xl text-ink-50 mt-1">Codice Partner</h2>
                        <p className="text-xs text-ink-400 mt-1 max-w-xl">
                            Sei parte di un gruppo (es. RE/MAX, Gabetti)? Inserisci il codice ufficiale del network per sbloccare la <strong className="text-ink-100">pubblicazione illimitata</strong> — inclusa nel tuo abbonamento da €{status.premium_price?.toFixed(0)}/mese.
                        </p>
                    </div>
                </div>
                <div data-testid="partnership-badge" className={`px-3 py-1.5 text-[11px] tracking-widest uppercase font-bold border ${isPartner ? "border-gold text-gold bg-gold/5" : "border-white/20 text-ink-400"}`}>
                    {isPartner ? "Partner attivo" : "Standard"}
                </div>
            </div>

            {/* CTA: piano non attivo */}
            {!premiumActive && (
                <div data-testid="partnership-need-premium" className="border border-amber-600/40 bg-amber-900/10 p-5 flex items-start gap-3">
                    <Lock size={16} className="text-amber-300 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="text-sm text-amber-100 font-medium">Prima attiva il piano Agenzia Pro (€{status.premium_price?.toFixed(0)}/mese)</div>
                        <p className="text-xs text-amber-200/80 mt-1">Il codice Partnership NON sostituisce il pagamento: potenzia i benefici del piano. Attiva prima l&apos;abbonamento €{status.premium_price?.toFixed(0)}, poi torna qui per inserire il codice.</p>
                        <Link data-testid="partnership-goto-pricing" to="/prezzi" className="mt-3 inline-flex items-center gap-2 cta-gold px-4 py-2 text-xs">
                            <Crown size={12} /> Attiva Agenzia Pro €{status.premium_price?.toFixed(0)}
                        </Link>
                    </div>
                </div>
            )}

            {/* Stato Partner attivo */}
            {premiumActive && isPartner && (
                <div data-testid="partnership-active-state" className="border border-gold/30 bg-gold/5 p-5">
                    <div className="flex items-center gap-2 text-gold mb-2">
                        <CheckCircle2 size={16} />
                        <span className="text-xs uppercase tracking-widest font-bold">Pubblicazione illimitata attiva</span>
                    </div>
                    <p className="text-sm text-ink-50 leading-relaxed">
                        Con il tuo codice Partnership <strong data-testid="partnership-code-display" className="text-gold">{status.partnership_code}</strong>, la pubblicazione illimitata è ora inclusa nel tuo abbonamento da 69€/mese.
                    </p>
                    <div className="flex flex-wrap items-center gap-3 mt-4">
                        <span className="inline-flex items-center gap-2 text-xs text-ink-100 border border-white/10 px-3 py-1.5">
                            <InfinityIcon size={12} className="text-gold" /> Slot annunci illimitati
                        </span>
                        {status.partnership_activated_at && (
                            <span className="text-[11px] text-ink-400">Attivo dal {new Date(status.partnership_activated_at).toLocaleDateString("it-IT")}</span>
                        )}
                        <button data-testid="partnership-revoke-btn" onClick={revoke} disabled={busy} className="ml-auto text-[11px] text-ink-400 hover:text-red-300 flex items-center gap-1 border border-white/10 px-3 py-1.5">
                            <X size={11} /> Disattiva
                        </button>
                    </div>
                </div>
            )}

            {/* Stato Standard (piano attivo) — form di inserimento codice */}
            {premiumActive && !isPartner && (
                <div className="border border-white/10 bg-ink-900/50 p-5">
                    {decay && (
                        <div data-testid="partnership-decay-warning" className="mb-4 border border-amber-600/40 bg-amber-900/10 p-3 flex items-start gap-2 text-xs text-amber-100">
                            <AlertTriangle size={14} className="text-amber-300 shrink-0 mt-0.5" />
                            <span>
                                {decay === "code_revoked" && `Il codice Partnership precedente è stato disattivato dall'amministratore. Inserisci un nuovo codice o resta su Standard (${status.included_slots} slot).`}
                                {decay === "code_deleted" && "Il codice Partnership precedente è stato eliminato. Contatta il tuo gruppo o inserisci un nuovo codice."}
                                {decay === "subscription_expired" && "La Partnership era decaduta per abbonamento scaduto. Ora che è nuovamente attivo, puoi riattivarla inserendo il codice."}
                            </span>
                        </div>
                    )}
                    <div data-testid="partnership-included-slots-text" className="text-sm text-ink-100 mb-3">Attualmente hai <strong className="text-ink-50">{status.included_slots} slot di pubblicazione inclusi</strong>. Con un codice Partner valido, gli slot diventano illimitati.</div>
                    <form onSubmit={redeem} className="flex flex-wrap gap-2">
                        <input
                            data-testid="partnership-code-input"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder="Inserisci Codice Partner (es. GRUPPO-REMAX)"
                            maxLength={32}
                            className="flex-1 min-w-[240px] bg-ink-950/60 border border-white/15 focus:border-gold outline-none px-4 py-2.5 text-sm tracking-wider"
                        />
                        <button data-testid="partnership-submit-btn" type="submit" disabled={busy || !code.trim()} className="cta-gold px-5 py-2.5 text-xs disabled:opacity-50">
                            {busy ? "Attivazione..." : "Attiva Codice"}
                        </button>
                    </form>
                    <p className="text-[11px] text-ink-400 mt-3">Non hai un codice? Chiedi al tuo gruppo di appartenenza (es. franchising RE/MAX, Gabetti, ecc.).</p>

                    {slots && (
                        <div className="mt-6 pt-5 border-t border-white/10">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs uppercase tracking-widest text-ink-400">Immobili pubblicati</span>
                                <span data-testid="listing-slots-counter" className="text-sm text-ink-50 font-medium">{slots.used} / {slots.total_limit}</span>
                            </div>
                            <div className="w-full h-1.5 bg-white/10">
                                <div className="h-full bg-gold" style={{ width: `${Math.min(100, (slots.used / Math.max(slots.total_limit, 1)) * 100)}%` }} />
                            </div>

                            <div className="text-xs uppercase tracking-widest text-ink-400 mt-6 mb-3">Acquista Slot Mensili</div>
                            <div className="flex flex-wrap gap-3">
                                {(slots.tiers || []).map((t) => {
                                    const active = slots.tier === t.id;
                                    return (
                                        <button
                                            key={t.id}
                                            data-testid={`slots-buy-${t.id}`}
                                            onClick={() => buySlots(t.id)}
                                            disabled={slotsBusy || active}
                                            className={`flex-1 min-w-[130px] border p-4 text-left transition-colors ${active ? "border-gold bg-gold/10" : "border-white/15 hover:border-gold/50"} disabled:opacity-80`}
                                        >
                                            <div className="text-lg font-serif text-ink-50">+{t.slots}</div>
                                            <div className="text-xs text-ink-400">€{t.price.toFixed(0)}/mese</div>
                                            {active && <div className="text-[10px] text-gold mt-1 uppercase tracking-wider">Attivo</div>}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-ink-400 mt-3">Un pacchetto sostituisce l&apos;altro (non si accumulano). Abbonamento Stripe ricorrente mensile, disdicibile in ogni momento.</p>
                            {slots.extra_slots > 0 && (
                                <button data-testid="slots-cancel-btn" onClick={cancelSlots} disabled={slotsBusy} className="mt-3 text-[11px] text-ink-400 hover:text-red-300 flex items-center gap-1">
                                    <X size={11} /> Disdici pacchetto slot
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {feedback && (
                <div
                    data-testid={`partnership-feedback-${feedback.type}`}
                    className={`mt-4 p-3 text-sm border ${feedback.type === "success" ? "border-green-500/40 bg-green-900/20 text-green-200" : "border-red-500/40 bg-red-900/20 text-red-200"}`}
                >
                    {feedback.msg}
                </div>
            )}
        </div>
    );
}
